import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {SafeHtml} from '@angular/platform-browser';

import {TranslateService} from '@ngx-translate/core';

import {takeUntil} from 'rxjs/operators';

import {escapeRegexp} from './common';
import {OptionsBehavior} from './select-interfaces';
import {SelectItem} from './select-item';
import {stripTags} from './select-pipes';

import {Destroyable} from '../../../destroyable.component';
import {isPresent} from '../../utils/lang';

@Component({
  selector: 'ng-select',
  styleUrls: ['./select.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ],
  templateUrl: './select.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectComponent extends Destroyable implements OnInit, ControlValueAccessor, OnChanges, OnDestroy {
  @Input() public parentScrollSelector: string;
  @Input() public allowClear: boolean = true;
  @Input() public placeholder: string = '';
  @Input() public customEmptyText = 'No users selected';
  @Input() public idField: string = 'id';
  @Input() public textField: string = 'text';
  @Input() public childrenField: string = 'children';
  @Input() public buttons: any;
  @Input() public isMultiSelect: boolean;
  @Input() public multiItemName: { single: string, plural: string } = {single: 'item', plural: 'items'};
  @Input() public asyncAutoCompletion: boolean = false;
  @Input() public sortByTranslationsEnabled: boolean = true;

  @Input()
  public set items(value: Array<any>) {
    if (!value) {
      this._items = this.itemObjects = [];
    } else {
      this._items = value.filter((item: any) => {
        if (
          (typeof item === 'string')
          || (typeof item === 'object' && item && item[this.textField] && item[this.idField])
        ) {
          return item;
        }
      });

      this.itemObjects = this._items.map((item: any) =>
        (typeof item === 'string' ?
          new SelectItem(item) :
          new SelectItem({
            id: item[this.idField],
            text: item[this.textField],
            children: item[this.childrenField],
            isChecked: item['isChecked'],
            isDisabled: item['isDisabled'] || (item['selectable'] === false) || (item['selectable'] === 'false'),
          }))
      );
    }

    if (this.isMultiSelect) {
      this.updateNumberOfChecked();
    }
  }

  @Input()
  public set readOnly(value: boolean) {
    this.disabled = value;
  }

  public get readOnly(): boolean {
    return this._disabled;
  }

  @Input()
  public set disabled(value: boolean) {
    this._disabled = value;
    if (this._disabled === true) {
      this.hideOptions();
    }
  }

  public get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  public set active(selectedItems: any) {
    this.selectedValue = selectedItems;
    if (selectedItems && (typeof selectedItems === 'string'
      || typeof selectedItems === 'number')) {
      selectedItems = this._items.filter((item: any) => item[this.idField].toString() === selectedItems.toString());
    }
    if (!selectedItems || selectedItems.length === 0) {
      this._active = [];
    } else {
      const areItemsStrings = typeof selectedItems[0] === 'string';

      this._active = selectedItems.map((item: any) => {
        const data = areItemsStrings
          ? item
          : {id: item[this.idField], text: item[this.textField]};

        return new SelectItem(data);
      });
    }
  }

  @ViewChild('mainElement', {static: true})
  public mainElement: ElementRef;

  @ViewChild('dropdown', {static: true})
  public dropdown: ElementRef;

  @Output() public data: EventEmitter<any> = new EventEmitter();
  @Output() public selected: EventEmitter<any> = new EventEmitter();
  @Output() public removed: EventEmitter<any> = new EventEmitter();
  @Output() public typed: EventEmitter<any> = new EventEmitter();
  @Output() public opened: EventEmitter<any> = new EventEmitter();
  @Output() public focus: EventEmitter<boolean> = new EventEmitter();
  @Output() public blur: EventEmitter<boolean> = new EventEmitter();
  @Output() public checked: EventEmitter<any> = new EventEmitter();
  @Output() public checkedAll: EventEmitter<any> = new EventEmitter();
  @Output() public clearedAll: EventEmitter<any> = new EventEmitter();

  public options: Array<SelectItem> = [];
  public itemObjects: Array<SelectItem> = [];
  public activeOption: SelectItem;
  public element: ElementRef;
  public inputMode: boolean = false;
  public triggerSortByTranslationPipe: boolean = true;

  public get active(): any {
    return this._active;
  }

  public set optionsOpened(value: boolean) {
    this._optionsOpened = value;
    this.opened.emit(value);
  }

  public get optionsOpened(): boolean {
    return this._optionsOpened;
  }

  public inputValue: string = '';
  protected onChange: any = Function.prototype;


  protected onTouched: any = Function.prototype;
  private _optionsOpened: boolean = false;
  private behavior: OptionsBehavior;
  private _items: Array<any> = [];
  private _disabled: boolean = false;
  private _active: Array<SelectItem> = [];
  private _numberOfChecked: number;
  private selectedValue: any;
  private removeClickOutsideListener: Function;

  @Input()
  public calcDropdownPosition = (viewportHeight: number,
                                 dropdown: any,
                                 mainElement: any): void => {
    const dropdownRect = dropdown.firstElementChild.getBoundingClientRect();
    if (dropdownRect.top + dropdownRect.height > viewportHeight + 50) {
      dropdown.firstElementChild.style.top = `${-dropdownRect.height - 30}px`;
    }
  }

  public constructor(element: ElementRef,
                     @Inject(forwardRef(() => TranslateService)) private translate: TranslateService,
                     private cd: ChangeDetectorRef,
                     private renderer: Renderer2) {
    super();
    this.element = element;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.items) {
      this.active = this.selectedValue;
    }

    if (changes.items && this.optionsOpened) {
      this.open();
      this.cd.markForCheck();
    }
  }

  public sanitize(html: string): SafeHtml {
    return html;
  }

  public inputEvent(e: any, isUpMode: boolean = false): void {
    // tab
    if (e.keyCode === 9) {
      return;
    }
    if (isUpMode && (e.keyCode === 37 || e.keyCode === 39 || e.keyCode === 38 ||
      e.keyCode === 40 || e.keyCode === 13)) {
      e.preventDefault();
      return;
    }
    // backspace
    if (!isUpMode && e.keyCode === 8) {
      const el: any = this.element.nativeElement
        .querySelector('div.ui-select-container > input');
      if (!el.value || el.value.length <= 0) {
        if (this.active.length > 0) {
          this.remove(this.active[this.active.length - 1]);
        }
        e.preventDefault();
      }
    }
    // esc
    if (!isUpMode && e.keyCode === 27) {
      this.hideOptions();
      this.element.nativeElement.children[0].focus();
      e.preventDefault();
      return;
    }
    // del
    if (!isUpMode && e.keyCode === 46) {
      if (this.active.length > 0) {
        this.remove(this.active[this.active.length - 1]);
      }
      e.preventDefault();
    }
    // left
    if (!isUpMode && e.keyCode === 37 && this._items.length > 0) {
      this.behavior.first();
      e.preventDefault();
      return;
    }
    // right
    if (!isUpMode && e.keyCode === 39 && this._items.length > 0) {
      this.behavior.last();
      e.preventDefault();
      return;
    }
    // up
    if (!isUpMode && e.keyCode === 38) {
      this.behavior.prev();
      e.preventDefault();
      return;
    }
    // down
    if (!isUpMode && e.keyCode === 40) {
      this.behavior.next();
      e.preventDefault();
      return;
    }
    // enter
    if (!isUpMode && e.keyCode === 13) {
      if (this.active.indexOf(this.activeOption) === -1) {
        this.selectActiveMatch();
        this.behavior.next();
      }
      e.preventDefault();
      return;
    }
    const target = e.target || e.srcElement;
    if (target && target.value) {
      this.inputValue = target.value;
      if (!this.asyncAutoCompletion) {
        this.behavior.filter(new RegExp(escapeRegexp(this.inputValue), 'ig'));
      }
      this.doEvent('typed', this.inputValue);
      if (!this.asyncAutoCompletion) {
        this.setDropdownPosition();
      }
    } else {
      this.inputValue = '';
      this.open();
    }
  }

  public ngOnInit(): any {
    this.behavior = (this.firstItemHasChildren) ?
      new ChildrenBehavior(this) : new GenericBehavior(this, this.translate);
    this.translate.onLangChange
      .asObservable()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.triggerSortByTranslationPipe = !this.triggerSortByTranslationPipe;
      });
  }

  public remove(item: SelectItem): void {
    if (this._disabled === true) {
      return;
    }
    this.active = [];
    this.data.next(this.active);
    this.doEvent('removed', item);
  }

  public doEvent(type: string, value: any): void {
    if ((this as any)[type]) {
      (this as any)[type].next(value);
    }

    this.onTouched();
    if (type === 'selected' || type === 'removed') {
      this.onChange(this.active[0] ? this.active[0].id : '');
    }
  }

  public clickedOutside(): void {
    this.hideOptions();
    this.inputValue = '';
    this.doEvent('typed', this.inputValue);
  }

  public addClickOutsideListener() {
    if (!this.optionsOpened) {
      this.removeClickOutsideListener = this.renderer.listen('document', 'click', (event) => {
        if (event.target && !this.element.nativeElement.contains(event.target)
        ) {
          this.clickedOutside();
        }
      });
    }
  }

  public get firstItemHasChildren(): boolean {
    return this.itemObjects[0] && this.itemObjects[0].hasChildren();
  }

  public writeValue(val: any): void {
    this.active = val;
    this.data.emit(this.active);
    this.cd.markForCheck();
  }

  public registerOnChange(fn: (_: any) => {}): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public matchClick(e: any): void {
    if (this._disabled === true) {
      return;
    }
    this.addClickOutsideListener();
    this.inputMode = !this.inputMode;
    if (this.inputMode === true) {
      this.focusToInput();
      this.open();
    }
  }

  public mainClick(event: any): void {
    if (this.inputMode === true || this._disabled === true) {
      return;
    }
    if (event.keyCode === 46) {
      event.preventDefault();
      this.inputEvent(event);
      return;
    }
    if (event.keyCode === 8) {
      event.preventDefault();
      this.inputEvent(event, true);
      return;
    }
    if (event.keyCode === 9 || event.keyCode === 13 ||
      event.keyCode === 27 || (event.keyCode >= 37 && event.keyCode <= 40)) {
      event.preventDefault();
      return;
    }
    this.inputMode = true;
    const value = String
      .fromCharCode(96 <= event.keyCode && event.keyCode <= 105 ? event.keyCode - 48 : event.keyCode)
      .toLowerCase();
    this.focusToInput(value);
    this.open();
    const target = event.target || event.srcElement;
    target.value = value;
    this.inputEvent(event);
  }

  public setCheck(item: SelectItem, value: boolean): void {
    item.isChecked = value;
    this.doEvent('checked', {
      id: item.id,
      value
    });
    value ? this.numberOfChecked++ : this.numberOfChecked--;
  }

  public selectAllCheck(value: boolean = true): void {
    const ids: string[] = [];
    this.options.forEach((item: SelectItem) => {
      item.isChecked = value;
      ids.push(item.id);
    });

    this.numberOfChecked = value ? this.options.length : 0;
    this.doEvent(value ? 'checkedAll' : 'clearedAll', ids);
  }

  public get numberOfChecked(): number {
    if (!isPresent(this._numberOfChecked)) {
      this.updateNumberOfChecked();
    }
    return this._numberOfChecked;
  }

  public set numberOfChecked(value: number) {
    this._numberOfChecked = value;
  }

  public updateNumberOfChecked(): void {
    this.numberOfChecked = this._items.reduce((prev: number, curr: SelectItem) => {
      return curr.isChecked ? ++prev : prev;
    }, 0);
  }

  public getPlaceholder(): string {
    if (!this.isMultiSelect) {
      return this.active.length <= 0 ? this.placeholder : '';
    }

    if (this.numberOfChecked) {
      return this.numberOfChecked > 1
        ? `${this.numberOfChecked} ${this.multiItemName.plural} selected`
        : `${this.numberOfChecked} ${this.multiItemName.single} selected`;
    } else {
      return this.translate.instant(`${this.customEmptyText}`);
    }
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    if (this.removeClickOutsideListener) {
      this.removeClickOutsideListener();
    }
  }

  public selectActive(value: SelectItem): void {
    this.activeOption = value;
  }

  public isActive(value: SelectItem): boolean {
    return this.activeOption.id === value.id;
  }

  public removeClick(value: SelectItem, event: any): void {
    event.stopPropagation();
    this.remove(value);
  }

  private focusToInput(value: string = ''): void {
    setTimeout(() => {
      const el = this.element.nativeElement.querySelector('div.ui-select-container > input');
      if (el) {
        el.focus();
        el.value = value;
      }
    }, 0);
  }

  private open(): void {
    this.options = [...this.itemObjects];

    if (this.options.length > 0) {
      this.behavior.first();
    }
    this.optionsOpened = true;

    this.setDropdownPosition();
  }

  private hideOptions(): void {
    if (this.removeClickOutsideListener) {
      this.removeClickOutsideListener();
    }
    this.inputMode = false;
    this.optionsOpened = false;
    this.cd.markForCheck();
  }

  private selectActiveMatch(): void {
    this.selectMatch(this.activeOption);
  }

  public selectMatch(value: SelectItem, e: Event = void 0): void {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (this.options.length <= 0) {
      return;
    }
    this.active = value.id;
    this.data.next(this.active[0]);
    this.doEvent('selected', value);
    this.hideOptions();
    this.focusToInput(stripTags(value.text));
    this.element.nativeElement.querySelector('.ui-select-container').focus();
  }

  private setDropdownPosition() {
    const dropdown = this.dropdown.nativeElement;
    const mainElement = this.mainElement.nativeElement;
    dropdown.style.visibility = 'hidden';

    setTimeout(() => {
      if (dropdown.firstElementChild) {
        dropdown.firstElementChild.style.top = '';
        const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
        this.calcDropdownWidth(mainElement, dropdown);
        this.calcDropdownPosition(viewportHeight, dropdown, mainElement);
      }

      dropdown.style.visibility = '';
      if (this.parentScrollSelector) {
        const parent = dropdown.closest(this.parentScrollSelector);
        if (parent && parent.scrollLeft) {
          dropdown.style.marginLeft = `-${parent.scrollLeft + 12}px`;
        }
      }
    }, 100);
  }

  private calcDropdownWidth(mainElement, dropdown): void {
    const inputRect = mainElement.lastElementChild.getBoundingClientRect();
    dropdown.firstElementChild.style.width = `${inputRect.width}px`;
  }
}

export class Behavior {
  public optionsMap: Map<string, number> = new Map<string, number>();

  public actor: SelectComponent;

  public constructor(actor: SelectComponent) {
    this.actor = actor;
  }

  public fillOptionsMap(): void {
    this.optionsMap.clear();
    let startPos = 0;
    this.actor.itemObjects
      .forEach((item: SelectItem) => {
        startPos = item.fillChildrenHash(this.optionsMap, startPos);
      });
  }

  public ensureHighlightVisible(optionsMap: Map<string, number> = void 0): void {
    const container = this.actor.element.nativeElement.querySelector('.ui-select-choices-content');
    if (!container) {
      return;
    }
    const choices = container.querySelectorAll('.ui-select-choices-row');
    if (choices.length < 1) {
      return;
    }
    const activeIndex = this.getActiveIndex(optionsMap);
    if (activeIndex < 0) {
      return;
    }
    const highlighted: any = choices[activeIndex];
    if (!highlighted) {
      return;
    }
    const posY: number = highlighted.offsetTop + highlighted.clientHeight - container.scrollTop;
    const height: number = container.offsetHeight;
    if (posY > height) {
      container.scrollTop += posY - height;
    } else if (posY < highlighted.clientHeight) {
      container.scrollTop -= highlighted.clientHeight - posY;
    }
  }

  private getActiveIndex(optionsMap: Map<string, number> = void 0): number {
    let ai = this.actor.options.indexOf(this.actor.activeOption);
    if (ai < 0 && optionsMap !== void 0) {
      ai = optionsMap.get(this.actor.activeOption.id);
    }
    return ai;
  }
}

export class GenericBehavior extends Behavior implements OptionsBehavior {
  public constructor(actor: SelectComponent, private translate: TranslateService) {
    super(actor);
  }

  public first(): void {
    this.actor.activeOption = this.actor.options[0];
    super.ensureHighlightVisible();
  }

  public last(): void {
    this.actor.activeOption = this.actor.options[this.actor.options.length - 1];
    super.ensureHighlightVisible();
  }

  public prev(): void {
    const index = this.actor.options.indexOf(this.actor.activeOption);
    this.actor.activeOption = this.actor
      .options[index - 1 < 0 ? this.actor.options.length - 1 : index - 1];
    super.ensureHighlightVisible();
  }

  public next(): void {
    const index = this.actor.options.indexOf(this.actor.activeOption);
    this.actor.activeOption = this.actor
      .options[index + 1 > this.actor.options.length - 1 ? 0 : index + 1];
    super.ensureHighlightVisible();
  }

  public filter(query: RegExp): void {
    const options = this.actor.itemObjects
      .filter((option: SelectItem) => stripTags(this.translate.instant(option.text)).match(query));

    this.actor.options = options;
    if (this.actor.options.length > 0) {
      this.actor.activeOption = this.actor.options[0];
      super.ensureHighlightVisible();
    }
  }
}

export class ChildrenBehavior extends Behavior implements OptionsBehavior {
  public constructor(actor: SelectComponent) {
    super(actor);
  }

  public first(): void {
    this.actor.activeOption = this.actor.options[0].children[0];
    this.fillOptionsMap();
    this.ensureHighlightVisible(this.optionsMap);
  }

  public last(): void {
    this.actor.activeOption =
      this.actor
        .options[this.actor.options.length - 1]
        .children[this.actor.options[this.actor.options.length - 1].children.length - 1];
    this.fillOptionsMap();
    this.ensureHighlightVisible(this.optionsMap);
  }

  public prev(): void {
    const indexParent = this.actor.options
      .findIndex(
        (option: SelectItem) => this.actor.activeOption.parent &&
          this.actor.activeOption.parent.id === option.id
      );
    const index = this.actor.options[indexParent].children
      .findIndex((option: SelectItem) => this.actor.activeOption && this.actor.activeOption.id === option.id);
    this.actor.activeOption = this.actor.options[indexParent].children[index - 1];
    if (!this.actor.activeOption) {
      if (this.actor.options[indexParent - 1]) {
        this.actor.activeOption = this.actor
          .options[indexParent - 1]
          .children[this.actor.options[indexParent - 1].children.length - 1];
      }
    }
    if (!this.actor.activeOption) {
      this.last();
    }
    this.fillOptionsMap();
    this.ensureHighlightVisible(this.optionsMap);
  }

  public next(): void {
    const indexParent = this.actor.options
      .findIndex(
        (option: SelectItem) => this.actor.activeOption.parent &&
          this.actor.activeOption.parent.id === option.id
      );
    const index = this.actor.options[indexParent].children
      .findIndex((option: SelectItem) => this.actor.activeOption && this.actor.activeOption.id === option.id);
    this.actor.activeOption = this.actor.options[indexParent].children[index + 1];
    if (!this.actor.activeOption) {
      if (this.actor.options[indexParent + 1]) {
        this.actor.activeOption = this.actor.options[indexParent + 1].children[0];
      }
    }
    if (!this.actor.activeOption) {
      this.first();
    }
    this.fillOptionsMap();
    this.ensureHighlightVisible(this.optionsMap);
  }

  public filter(query: RegExp): void {
    const options: Array<SelectItem> = [];
    const optionsMap: Map<string, number> = new Map<string, number>();
    let startPos = 0;
    for (const si of this.actor.itemObjects) {
      const children: Array<SelectItem> = si.children.filter((option: SelectItem) => query.test(option.text));
      startPos = si.fillChildrenHash(optionsMap, startPos);
      if (children.length > 0) {
        const newSi = si.getSimilar();
        newSi.children = children;
        options.push(newSi);
      }
    }
    this.actor.options = options;
    if (this.actor.options.length > 0) {
      this.actor.activeOption = this.actor.options[0].children[0];
      super.ensureHighlightVisible(optionsMap);
    }
  }
}
